import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getApiConfig } from '../config/api';
import { tokenService } from './tokenService';
import {
  notifyAuthSessionExpired,
  SESSION_EXPIRED_MESSAGE,
} from './authSession';
import { logInfo, logWarn, logError } from '../utils/logger';
import { NotificationTemplateRequest } from '../types';
import { isSupportedApiPath, recordBlocked } from './apiWhitelist';

// Custom error class for authentication errors
class AuthenticationError extends Error {
  public status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AuthenticationError';
    this.status = status;
  }
}

const API_BASE_URL = getApiConfig().BACKEND_BASE_URL;

// Create axios instance with default config
export const api = axios.create({
  baseURL: API_BASE_URL,
  // Removed timeout for better user experience
});

// Initialize token service
tokenService.initializeToken();

const isPublicEndpoint = (url: string): boolean => (
  url.includes('/auth/login') ||
  url.includes('/auth/refresh') ||
  url.includes('/auth/sso/authorize-url') ||
  url.includes('/auth/sso/callback') ||
  url.includes('/api/auth/') ||
  url.includes('/health') ||
  url.includes('/api/health/')
);

const isRefreshEndpoint = (url: string): boolean => url.includes('/auth/refresh');

const isAuthLoginEndpoint = (url: string): boolean => (
  url.includes('/auth/login') ||
  url.includes('/auth/sso/authorize-url') ||
  url.includes('/auth/sso/callback')
);

const createSessionExpiredError = (status = 401): AuthenticationError => (
  new AuthenticationError(SESSION_EXPIRED_MESSAGE, status)
);

// Sentinel adapter that short-circuits a request — resolves immediately with
// an empty response so the caller doesn't crash on a missing endpoint.
const blockedAdapter = async (config: InternalAxiosRequestConfig): Promise<AxiosResponse> => {
  recordBlocked(config.url || '(unknown)');
  // Return an empty array for list-like GETs, null otherwise.
  const looksLikeList =
    config.method?.toLowerCase() === 'get' &&
    /\/(list|paginated|active|latest|stats|all|items|users|notifications|templates|conversations|collections|upcoming|subscribe|vapid-public-key)\b/.test(
      config.url || ''
    );
  return {
    data: looksLikeList ? [] : null,
    status: 200,
    statusText: 'OK (blocked — endpoint not in SmartCHG)',
    headers: {},
    config,
  } as AxiosResponse;
};

// Add request interceptor to include auth token + short-circuit unsupported endpoints.
api.interceptors.request.use(
  async (config) => {
    // --- 0) Short-circuit any call that SmartCHG backend does not serve ---
    if (!isSupportedApiPath(config.url)) {
      config.adapter = blockedAdapter as any;
      return config; // short-circuits through blockedAdapter without network I/O
    }

    // Skip authentication for truly-public endpoints. The `/auth/*` tree is
    // mixed: login/refresh/SSO-handshake are public, but everything else
    // under /auth/ (e.g. /auth/sap/connect, /auth/sap/connection-status)
    // is authenticated — so we enumerate the public auth paths explicitly
    // instead of matching `/auth/` as a blanket prefix.
    const url = config.url || '';
    const isPublic = isPublicEndpoint(url);

    if (!isPublic) {
      let token = tokenService.getToken();
      // Proactively refresh an expired (or missing) access token *before*
      // dispatching the request. Without this, the very first protected
      // request after a >1h idle period always 401s — the response
      // interceptor below would then refresh and retry, but every protected
      // endpoint emits a noisy `auth.token_expired` warning on the backend
      // and pays a round-trip. `tokenService.refreshToken()` already
      // de-dupes concurrent callers via its internal `tokenRefreshPromise`,
      // so a burst of parallel requests at app boot triggers exactly one
      // /auth/refresh call.
      if (token && tokenService.isTokenExpired()) {
        let refreshed: string | null = null;
        try {
          refreshed = await tokenService.refreshToken();
        } catch (refreshErr: unknown) {
          logError('API', 'Token refresh error before request - logging out user', refreshErr instanceof Error ? refreshErr : new Error('Unknown error'));
          notifyAuthSessionExpired('token_refresh_failed');
          throw createSessionExpiredError();
        }

        if (refreshed) {
          token = refreshed;
        } else {
          logWarn('API', 'Token refresh failed before request - logging out user');
          notifyAuthSessionExpired('token_refresh_failed');
          throw createSessionExpiredError();
        }
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        logInfo('API', `Adding auth header for protected endpoint: ${config.url}`);
      } else {
        logWarn('API', `No token found for protected endpoint: ${config.url}`);
      }
    } else {
      logInfo('API', `Public endpoint, no auth header needed: ${config.url}`);
    }

    // Set Content-Type for JSON requests only
    if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }

    // Log multipart requests for debugging
    if (config.data instanceof FormData) {

      // Ensure Content-Type is not set for multipart requests
      delete config.headers['Content-Type'];

    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const requestUrl = originalRequest.url || '';

    if (isRefreshEndpoint(requestUrl)) {
      logWarn('API', `Refresh API failed - logging out user. Status: ${status ?? 'network_error'}`);
      notifyAuthSessionExpired('token_refresh_failed');
      return Promise.reject(createSessionExpiredError(status ?? 401));
    }

    if ((status === 401 || status === 403) && isAuthLoginEndpoint(requestUrl)) {
      return Promise.reject(error);
    }

    // Handle 401/403 auth errors
    if ((status === 401 || status === 403) && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Attempt token refresh
        const newToken = await tokenService.refreshToken();
        if (newToken) {
          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } else {
          // Token refresh failed - trigger logout
          logWarn('API', 'Token refresh failed - logging out user');
          notifyAuthSessionExpired('token_refresh_failed');
          
          // Create a more user-friendly error for auth failures
          return Promise.reject(createSessionExpiredError(status));
        }
      } catch (refreshErr: unknown) {
        logError('API', 'Token refresh error - logging out user', refreshErr instanceof Error ? refreshErr : new Error('Unknown error'));
        notifyAuthSessionExpired('token_refresh_failed');

        // Create a more user-friendly error for auth failures
        return Promise.reject(createSessionExpiredError(status));
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
// SmartCHG backend paths:
//   POST /auth/login                  → TokenResponse { access_token, refresh_token, ... }
//   POST /auth/refresh                body { refresh_token }  → TokenResponse
//   GET  /auth/sso/authorize-url      → { authorization_url, state }
//   POST /auth/sso/callback           body { code?, state?, error?, error_description? }
//                                     → { token, id, name, email, role, department, refreshToken, employeeId }
//   GET  /api/users/profile           → { id, firstName, lastName, email, role, department,
//                                          officeLocation, employeeId, permissions[] }
//
// `authAPI.login` adapts /auth/login + /api/users/profile into the frontend
// { token, id, name, email, role, ... } shape AuthContext expects.
export interface UserProfileResponse {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  department: string | null;
  officeLocation: string | null;
  enabled: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  lastLogin: string | null;
  employeeId: string | null;
  permissions: string[];
  displayName: string | null;
  preferredLanguage: string | null;
  defaultDatasourceId: string | null;
  isNewUser: boolean;
  isNewlyAddedUser: boolean;
  welcomeQuestions: string[];
}

export const authAPI = {
  login: async (credentials: { email: string; password: string }) => {
    const tokenResp = await api.post<{
      access_token: string;
      refresh_token: string;
      token_type: string;
      expires_in: number;
    }>('/auth/login', credentials);
    const { access_token, refresh_token } = tokenResp.data;

    const profileResp = await api.get<UserProfileResponse>('/api/users/profile', {
      headers: { Authorization: `Bearer ${access_token}` },
    });
    const profile = profileResp.data;
    const fullName =
      [profile.firstName, profile.lastName].filter(Boolean).join(' ').trim() || profile.email;

    return {
      data: {
        token: access_token,
        refreshToken: refresh_token,
        id: profile.id,
        email: profile.email,
        name: fullName,
        role: profile.role,
        department: profile.department,
        officeLocation: profile.officeLocation,
        employeeId: profile.employeeId,
        permissions: profile.permissions,
        isNewUser: profile.isNewlyAddedUser === true,
      },
    };
  },

  refresh: (token: string) => api.post('/auth/refresh', { refresh_token: token }),

  // SSO (Microsoft Entra ID) API
  getSSOAuthorizationUrl: () => {
    return api.get('/auth/sso/authorize-url');
  },

  handleSSOCallback: (
    code?: string,
    state?: string,
    errorParam?: string,
    errorDescription?: string
  ) => {
    const body: Record<string, string> = {};
    if (code) body.code = code;
    if (state) body.state = state;
    if (errorParam) body.error = errorParam;
    if (errorDescription) body.error_description = errorDescription;
    return api.post('/auth/sso/callback', body);
  },

  // SAP connection status; OAuth still starts through /auth/sap/authorize-url
  // and finishes through /auth/sap/callback-exchange.
  getSapConnectionStatus: () => {
    return api.get('/auth/sap/connection-status');
  },
};

// Notification API
export const notificationAPI = {
  getAll: (params?: { page?: number; limit?: number; sort?: string }) => {
    const queryParams = params ? new URLSearchParams({
      ...(params.page && { page: params.page.toString() }),
      ...(params.limit && { limit: params.limit.toString() }),
      ...(params.sort && { sort: params.sort })
    }).toString() : '';
    return api.get(`/api/notifications${queryParams ? `?${queryParams}` : ''}`);
  },
  getPaginated: (page: number = 1, limit: number = 50) =>
    api.get(`/api/notifications/paginated?page=${page}&limit=${limit}&sortBy=createdAt&sortOrder=desc`),
  create: (notification: any) => api.post('/api/notifications', notification),
  markAsRead: (id: string) => api.patch(`/api/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/api/notifications/read-all'),
  getUnreadCount: () => api.get('/api/notifications/unread-count'),
  delete: (id: string) => api.delete(`/api/notifications/${id}`),
  deleteAll: () => api.delete('/api/notifications'),
  getDetails: (id: string) => api.get(`/api/notifications/${id}/details`),
};

// Notification Template API
export const notificationTemplateAPI = {
  getAll: () => api.get('/api/notification-templates'),
  getById: (id: string) => api.get(`/api/notification-templates/${id}`),
  create: (template: NotificationTemplateRequest) => api.post('/api/notification-templates', template),
  update: (id: string, template: NotificationTemplateRequest) => api.put(`/api/notification-templates/${id}`, template),
  delete: (id: string) => api.delete(`/api/notification-templates/${id}`),
  toggleStatus: (id: string) => api.patch(`/api/notification-templates/${id}/toggle`),
  process: (id: string, variables: Record<string, string>) => api.post(`/api/notification-templates/${id}/process`, variables),
  getVariables: (id: string) => api.get(`/api/notification-templates/${id}/variables`),
  getByType: (type: string) => api.get(`/api/notification-templates/type/${type}`),
};

// Conversation API — SmartCHG threads.
//
// Contracts (BE-2):
//   GET    /api/v1/threads                  → { threads: [{ thread_id, title,
//                                                created_at, updated_at,
//                                                message_count }] }   (updated_at DESC)
//   GET    /api/v1/threads/{id}/messages    → { thread: {...}, messages: [{
//                                                message_id, role, content,
//                                                created_at, reasoning_steps?,
//                                                references? }] }     (created_at ASC)
//   DELETE /api/v1/threads/{id}             → 204
//   PATCH  /api/v1/threads/{id}             body { title }
//                                           → { thread_id, title, updated_at }
//
export const conversationAPI = {
  listThreads: (params?: { search?: string; limit?: number; offset?: number }) => api.get('/api/v1/threads', { params }),

  getThreadMessages: (
    threadId: string,
    config?: { signal?: AbortSignal; timeout?: number },
  ) => api.get(`/api/v1/threads/${threadId}/messages`, config),

  deleteThread: (threadId: string) =>
    api.delete(`/api/v1/threads/${threadId}`),

  renameThread: (threadId: string, title: string) =>
    api.patch(`/api/v1/threads/${threadId}`, { title }),

  setThreadFavourite: (threadId: string, isFavourite: boolean) =>
    api.patch(`/api/v1/threads/${threadId}/favourite`, { is_favourite: isFavourite }),

};

// User profile preferences DTO. Shape matches BE-3's
// PATCH /api/users/profile body and the GET /api/users/profile response
// extensions (CLAUDE.md C3 contract).
export interface ProfileUpdatePayload {
  displayName?: string | null;
  preferredLanguage?: string | null;
  defaultDatasourceId?: string | null;
}

// Datasource summary returned by GET /api/v1/datasources
// (DatasourceResponse — see backend/application/dto.py).
export interface DatasourceSummary {
  datasource_id: string;
  name: string;
  type: string;
  description?: string | null;
  is_active: boolean;
  created_at?: string | null;
}

// Thin SmartCHG datasource API.
export const datasourceAPI = {
  list: () => api.get<DatasourceSummary[]>('/api/v1/datasources'),
};

export interface ClearCachePayload {
  pattern: string;
}

export interface ClearCacheResponse {
  pattern: string;
  deleted_count: number;
}

export const adminSettingsAPI = {
  clearCache: (payload: ClearCachePayload) =>
    api.post<ClearCacheResponse>('/api/v1/admin/cache/clear', payload),
};

// User API
export const userAPI = {
  getAll: () => api.get('/api/users'),
  getById: (id: string) => api.get(`/api/users/${id}`),
  update: (id: string, userData: any) => api.put(`/api/users/${id}`, userData),
  delete: (id: string) => api.delete(`/api/users/${id}`),
  getProfile: () => api.get<UserProfileResponse>('/api/users/profile'),
  // Backend exposes a single profile endpoint; alias kept for legacy callers.
  getComprehensiveProfile: () => api.get<UserProfileResponse>('/api/users/profile'),
  // PATCH /api/users/profile { displayName?, preferredLanguage?, defaultDatasourceId? }
  // → returns the same shape as GET /api/users/profile (full profile after update).
  updateProfile: (payload: ProfileUpdatePayload) =>
    api.patch<UserProfileResponse>('/api/users/profile', payload),
  getStats: () => api.get('/api/users/stats'),
  changePassword: (passwordData: any) => api.post('/api/users/change-password', passwordData),
  updateIntegrationIds: (integrationIds: { gmailId?: string | null; slackId?: string | null; teamId?: string | null }) =>
    api.post('/api/users/update-integration-ids', integrationIds),
  // Preferences
  getPreferences: () => api.get('/api/user-preferences'),
  savePreferences: (prefs: any) => api.post('/api/user-preferences', prefs),

  // Notification Settings
  getNotificationSettings: () => api.get('/api/user-settings/notifications'),
  updateNotificationSettings: (settings: any) => api.put('/api/user-settings/notifications', settings),

};

// Push Notification API
export const pushNotificationAPI = {
  // Get VAPID public key
  getVapidPublicKey: async () => {
    return api.get('/api/push-notifications/vapid-public-key');
  },

  // Subscribe to push notifications
  subscribe: async (subscription: any) => {
    return api.post('/api/push-notifications/subscribe', subscription);
  },

  // Unsubscribe from push notifications
  unsubscribe: async () => {
    return api.post('/api/push-notifications/unsubscribe');
  },

  // Get subscription status
  getStatus: async () => {
    return api.get('/api/push-notifications/status');
  }
};

// ============================================================================
// Smart Insights data-source management API
// ============================================================================

// Current Smart Insights helpers use the SmartCHG backend directly.

// Smart Insights dashboard response types
export interface ChartResponse {
  title: string;
  spec: Record<string, any>;
  data?: Array<Record<string, any>>;
  chart_type: string;
  data_key: string;
  alternative_types?: string[];
  x_field?: string;
  y_field?: string | string[];
  /** Human-readable X axis label (e.g. from backend chart_response.x_axis_label) */
  x_axis_label?: string;
  /** Human-readable Y axis label (e.g. from backend chart_response.y_axis_label) */
  y_axis_label?: string;
  /** Map of data column key -> display name for tooltips, legend, and drill-down table headers */
  metric_display_names?: Record<string, string>;
  group_by?: string;
  /** Sort applied to chart data (e.g. "asc", "desc", or field-based sort spec) */
  sort_by?: string;
  sort_order?: string | Record<string, unknown>;
  /** Chart-level filters applied (date range, etc.) */
  filters?: unknown;
  data_row_count?: number;
  aggregation_type?: string;
  aggregation_status?: string;
  aggregation_issues?: string[];
  field_validation?: Record<string, boolean>;
  original_row_count?: number;
  /** Backend-assigned category for story grouping (e.g. "revenue", "volume") */
  category?: string;
  /** Alternative key used by some backends for category grouping */
  group?: string;
}

export interface DashboardResponse {
  query: string;
  task?: string;
  charts: ChartResponse[];
  insights: string[];
  analysis_summary?: {
    summary_text?: string;
    confidence?: 'high' | 'medium' | 'low';
    confidence_reason?: string;
    date_filter?: {
      applied: boolean;
      start_date?: string;
      end_date?: string;
    };
    metrics_to_display?: string[];
    /** Per-category summaries for story layout. Array: [{ category?, group?, text?, summary?, content? }] or Object: { [category]: string } */
    summaries_by_group?: Array<{ category?: string; group?: string; name?: string; text?: string; summary?: string; content?: string }> | Record<string, string | { text?: string }>;
  };
  statistics?: Record<string, any>;
  metadata?: Record<string, any>;
  status: string;
  errors: string[];
  date_grouping?: Record<string, any>;
  computation_results?: Array<{
    metric: string;
    value: any;
    status: string;
    error?: string;
    category?: string;
    group?: string;
  }>;
  sql_plan?: Record<string, any>;
  generated_sql?: string;
  selected_tables?: string[];
  chart_plan?: Record<string, any>;
  operation_plan?: Record<string, any>;
  source_data?: Record<string, Array<Record<string, any>>>;
  source_data_metadata?: Record<string, {
    total_rows: number;
    preview_rows: number;
    is_truncated: boolean;
    columns: string[];
    dtypes: Record<string, string>;
    query_id: string;
    table_name: string;
    download_available: boolean;
    download_message?: string;
  }>;
  intelligence_analysis?: {
    level_1_descriptive: {
      summary: string;
      variance_analysis: Array<{
        metric: string;
        actual: string | number;
        variance: string;
        explanation: string;
      }>;
      drill_down_insights: Array<{
        dimension: string;
        finding: string;
        supporting_metrics?: string[];
      }>;
    };
    level_2_diagnostic: {
      summary: string;
      root_causes: Array<{
        title: string;
        description: string;
        impact?: string;
        supporting_metrics?: string[];
      }>;
      correlations: Array<{
        factor_a: string;
        factor_b: string;
        relationship: string;
        strength: string;
      }>;
    };
    level_3_predictive: {
      summary: string;
      forecasts: Array<{
        metric: string;
        forecast_value: string | number;
        time_horizon: string;
        confidence_range?: string;
        assumptions: string[];
        trend: string;
      }>;
      early_warnings: Array<{
        warning: string;
        metric: string;
        threshold?: string;
        current_value?: string;
        time_to_breach?: string;
      }>;
    };
    level_4_prescriptive: {
      summary: string;
      recommendations: Array<{
        title: string;
        description: string;
        category: string;
        priority: string;
        action_steps: string[];
        expected_impact?: string;
        risk_assessment?: string;
        supporting_metrics?: string[];
      }>;
      scenarios: Array<{
        scenario_name: string;
        description: string;
        outcome: string;
        assumptions: string[];
      }>;
    };
    level_5_strategic: {
      summary: string;
      capital_allocation: Array<{
        area: string;
        current_roic?: string;
        recommendation: string;
        rationale: string;
      }>;
      unit_economics: Array<{
        metric: string;
        finding: string;
        implication: string;
      }>;
      strategic_insights: Array<{
        insight: string;
        time_horizon: string;
        impact: string;
      }>;
    };
    summary: string;
  };
  // Optional plain text fallback when no charts/metrics are available
  normal_text?: string;
  suggested_queries?: string[];
}

export interface DataSourceTable {
  name: string;
  columns: string[];
  column_count: number;
}

export interface DataSource {
  id?: number;
  user_id?: string;
  name: string;
  type: string;
  host?: string;
  port?: number;
  username?: string;
  password?: string;
  database_name?: string;
  file_path?: string;
  is_active?: boolean;
  created_at?: string;
  tables?: DataSourceTable[]; // Tables/sheets available in this data source
}

export interface DataSourceCreateRequest {
  user_id: string;
  name: string;
  type: 'postgres' | 'sqlserver' | 'sap' | 'excel' | 'csv';
  host?: string;
  port?: number;
  username?: string;
  password?: string;
  database_name?: string;
  file_path?: string;
}

export interface DataSourceTestResponse {
  success: boolean;
  message: string;
}

/**
 * SmartCHG analysis-row shape. ``id`` and ``data_source_id`` carry the
 * canonical UUID strings the backend returns; legacy numeric duplicates
 * are populated by the helper below so existing UI lookups (which key by
 * ``source.id`` — a numeric synthetic React key) keep working.
 */
export interface DataSourceAnalysisStatus {
  id: number; // legacy synthetic numeric — derived in the helper for backward compat
  analysis_id: string; // canonical UUID from SmartCHG
  data_source_id: number; // legacy synthetic numeric duplicate
  data_source_uuid: string; // canonical UUID from SmartCHG
  user_id?: string;
  description?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  progress_percent: number;
  current_table?: string;
  total_tables: number;
  processed_tables: number;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at?: string;
}

export interface ColumnDescription {
  id: number; // legacy synthetic
  col_id: string; // canonical UUID
  analysis_id: string; // canonical UUID
  data_source_id: number; // legacy synthetic
  data_source_uuid: string; // canonical UUID
  table_name: string;
  column_name: string;
  data_type?: string;
  unique_values?: any[];
  description: string;
  usage_suggestions: string;
  created_at: string;
}

/**
 * Stable hash from a UUID string to a positive 32-bit integer. Used to
 * synthesize a numeric ``id`` / ``data_source_id`` so the existing UI keying
 * (which assumed ints in the legacy schema) keeps working without a wider
 * refactor. Two callers passing the same UUID get the same int back.
 */
export function uuidToInt(uuid: string | null | undefined): number {
  const s = String(uuid ?? '');
  if (!s) return 0;
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  }
  // Clamp to non-negative 31-bit range so JSON / React keys stay tidy.
  return Math.abs(h) || 1;
}

function _coerceAnalysis(raw: any): DataSourceAnalysisStatus {
  const dsUuid: string = String(raw?.data_source_id ?? '');
  const aUuid: string = String(raw?.analysis_id ?? '');
  return {
    id: uuidToInt(aUuid),
    analysis_id: aUuid,
    data_source_id: uuidToInt(dsUuid),
    data_source_uuid: dsUuid,
    user_id: raw?.user_id,
    description: raw?.description,
    status: raw?.status,
    progress_percent: Number(raw?.progress_percent ?? 0),
    current_table: raw?.current_table ?? undefined,
    total_tables: Number(raw?.total_tables ?? 0),
    processed_tables: Number(raw?.processed_tables ?? 0),
    error_message: raw?.error_message ?? undefined,
    started_at: raw?.started_at ?? undefined,
    completed_at: raw?.completed_at ?? undefined,
    created_at: raw?.created_at,
    updated_at: raw?.updated_at ?? undefined,
  };
}

function _coerceColumn(raw: any): ColumnDescription {
  const dsUuid: string = String(raw?.datasource_id ?? raw?.data_source_uuid ?? '');
  return {
    id: uuidToInt(String(raw?.col_id ?? '')),
    col_id: String(raw?.col_id ?? ''),
    analysis_id: String(raw?.analysis_id ?? ''),
    data_source_id: uuidToInt(dsUuid),
    data_source_uuid: dsUuid,
    table_name: String(raw?.table_name ?? ''),
    column_name: String(raw?.column_name ?? ''),
    data_type: raw?.data_type ?? undefined,
    unique_values: Array.isArray(raw?.unique_values) ? raw.unique_values : undefined,
    description: String(raw?.description ?? ''),
    usage_suggestions: String(raw?.usage_suggestions ?? ''),
    created_at: String(raw?.created_at ?? ''),
  };
}

/**
 * Start a data source analysis (SmartCHG /api/v1/datasource_analysis/*).
 *
 * The first arg accepts either:
 *   * the canonical datasource UUID string (preferred — what SmartCHG keys on); or
 *   * the legacy numeric synthetic id, which is treated as opaque and best-
 *     effort handed to the backend (fails 422 if the backend can't parse it
 *     as a UUID — caller should pass a real UUID).
 *
 * ``userId`` is accepted for signature parity with the legacy CHG-AI-Platform
 * shim but is ignored — SmartCHG identifies the principal from the bearer
 * token and runs ownership / role checks itself.
 */
export async function startDataSourceAnalysis(
  dataSourceId: string | number,
  _userId: string,
  description: string,
): Promise<{ analysis_id: string; datasource_id: string; status: string }> {
  try {
    const path = `/api/v1/datasource_analysis/start/${encodeURIComponent(String(dataSourceId))}`;
    const response = await api.post(path, { description });
    return {
      analysis_id: String(response.data?.analysis_id ?? ''),
      datasource_id: String(response.data?.datasource_id ?? ''),
      status: String(response.data?.status ?? 'started'),
    };
  } catch (error: any) {
    logError('API', 'Failed to start data source analysis', error);
    throw new Error(`Failed to start analysis: ${error.response?.data?.detail || error.response?.data?.message || error.message}`);
  }
}

/**
 * Get all active analyses (status IN (pending, in_progress)).
 */
export async function getActiveAnalyses(): Promise<DataSourceAnalysisStatus[]> {
  try {
    const response = await api.get('/api/v1/datasource_analysis/active');
    const rows = Array.isArray(response.data) ? response.data : [];
    return rows.map(_coerceAnalysis);
  } catch (error: any) {
    logError('API', 'Failed to get active analyses', error);
    throw new Error(`Failed to get active analyses: ${error.response?.data?.detail || error.response?.data?.message || error.message}`);
  }
}

/**
 * Get the latest analysis per datasource (any status).
 */
export async function getLatestAnalyses(): Promise<DataSourceAnalysisStatus[]> {
  try {
    const response = await api.get('/api/v1/datasource_analysis/latest');
    const rows = Array.isArray(response.data) ? response.data : [];
    return rows.map(_coerceAnalysis);
  } catch (error: any) {
    logError('API', 'Failed to get latest analyses', error);
    throw new Error(`Failed to get latest analyses: ${error.response?.data?.detail || error.response?.data?.message || error.message}`);
  }
}

/**
 * Get column descriptions for a completed analysis. ``analysisId`` is the
 * canonical UUID string returned by ``startDataSourceAnalysis`` — passing
 * the synthetic numeric id from the analysis-status row will 404.
 */
export async function getColumnDescriptions(analysisId: string | number): Promise<ColumnDescription[]> {
  try {
    const response = await api.get(`/api/v1/datasource_analysis/columns/${encodeURIComponent(String(analysisId))}`);
    const rows = Array.isArray(response.data) ? response.data : [];
    return rows.map(_coerceColumn);
  } catch (error: any) {
    logError('API', 'Failed to get column descriptions', error);
    throw new Error(`Failed to get column descriptions: ${error.response?.data?.detail || error.response?.data?.message || error.message}`);
  }
}

/**
 * Single-row poll — the canonical "is my analysis done yet" endpoint.
 */
export async function getAnalysisStatus(analysisId: string): Promise<DataSourceAnalysisStatus | null> {
  try {
    const response = await api.get(`/api/v1/datasource_analysis/status/${encodeURIComponent(String(analysisId))}`);
    return response.data ? _coerceAnalysis(response.data) : null;
  } catch (error: any) {
    if (error?.response?.status === 404) return null;
    logError('API', 'Failed to get analysis status', error);
    throw new Error(`Failed to get analysis status: ${error.response?.data?.detail || error.response?.data?.message || error.message}`);
  }
}
